import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChevronDownIcon, ShieldCheckIcon } from 'lucide-react';
import { checkoutUrl, images } from '../data/content';
import { trackInitiateCheckout } from '../utils/metaPixel';
import { rewarmCheckoutConnection } from '../utils/preconnect';
import { withUtms } from '../utils/withUtms';
import { useRemainingSpots } from '../hooks/useRemainingSpots';
import { CheckoutCover } from './CheckoutCover';
import { OfferCountdown } from './OfferCountdown';
export function PurchaseSection() {
  const [isGuaranteeOpen, setIsGuaranteeOpen] = useState(false);
  const [isCovered, setIsCovered] = useState(false);
  const isLeavingRef = useRef(false);
  const remainingSpots = useRemainingSpots();

  // iOS Safari freezes this page in the back-forward cache instead of unloading
  // it, so coming back from the store restores the old React state verbatim.
  // That left `isLeavingRef` stuck at true and every later click silently
  // ignored. Re-arm the button (and the page scroll) whenever it is restored.
  useEffect(() => {
    const reset = () => {
      isLeavingRef.current = false;
      setIsCovered(false);
      document.body.style.overflow = '';
    };

    // A page restored from bfcache must never come back still covered.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) reset();
    };
    reset();
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', reset);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', reset);
    };
  }, []);

  // The cover is painted in the same commit as the navigation starts — flushSync
  // forces that frame first, so the landing never flashes underneath. Nothing is
  // awaited in between; the pixel call is fire-and-forget.
  const handleCheckoutClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    rewarmCheckoutConnection();
    trackInitiateCheckout();
    const destination = withUtms(checkoutUrl);
    flushSync(() => setIsCovered(true));
    window.location.href = destination;

    // Safety net: if the navigation is blocked or cancelled, don't leave the
    // button dead. Two seconds is far longer than a real redirect takes.
    window.setTimeout(() => {
      isLeavingRef.current = false;
    }, 2000);
  };
  return <section id="buy" className="border-y border-cream-300 bg-white px-5 py-14 sm:px-6 md:px-8" data-id="element-160" data-mp="1sy2r9ltzjche">
      <div className="mx-auto grid max-w-[980px] items-start gap-9 md:grid-cols-2 md:gap-12" data-id="element-161" data-mp="27vdbbjkp1qoq">
        <img src={images.heroTablets} alt="The six Little Engineers digital books shown on tablets, with sample activity pages above and finished cardboard projects below" className="mx-auto block h-auto w-full max-w-[460px] object-contain" width={1536} height={1024} loading="lazy" decoding="async" data-id="element-162" data-mp="22w0fi9buh9a0" />

        <div data-id="element-163" data-mp="2p463yjktrl96">
          <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-ink-500" data-id="element-164" data-mp="2p3wcerk4t61d">
            +3,369 downloads
          </p>
          <h2 className="mt-2.5 font-display text-[29px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink-900 md:text-[32px]" data-id="element-165" data-mp="2aegpxnrmvmrh">
            Little Engineers Digital Activity Collection
          </h2>

          <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-2" data-id="element-166" data-mp="21d0dlah9dakm">
            <span className="font-display text-[28px] font-semibold tracking-[-0.02em] text-ink-900" data-id="element-167" data-mp="25h8339vjiujw">
              $27.99
            </span>
            <span className="text-[16px] text-ink-500 line-through" data-id="element-168" data-mp="1rii9i47xjd0a">$55.98 USD</span>
            <span className="rounded-md bg-sun-400/20 px-2 py-[3px] text-[12px] font-semibold uppercase tracking-[0.08em] text-gold-600" data-id="element-169" data-mp="1wfqs25wzpj22">
              -50% off
            </span>
          </div>

          <a href={checkoutUrl} onClick={handleCheckoutClick} className="btn-cta mt-7 flex w-full items-center justify-center rounded-xl px-6 py-4 text-center font-display text-[17px] font-semibold tracking-[0.02em] text-white transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-forest-500/35" data-id="element-170" data-mp="3qszuxev166bc">
            Get Instant Access
          </a>

          <p className="mt-3 flex items-center justify-center gap-2 text-[13.5px] text-ink-500" data-id="element-171" data-mp="1ctlgfvo15cz9">
            <ShieldCheckIcon className="h-4 w-4 text-forest-500" aria-hidden="true" data-id="element-172" data-mp="17uh6h2ta7b7v" />
            Instant access by email · secure payment
          </p>
          <p className="mt-2 text-center text-[12.5px] text-ink-500" data-id="element-173" data-mp="y1tdkj5f9ref">
            100% Digital Product — No physical item will be shipped.
          </p>

          <p className="mt-8 flex items-center justify-center gap-2.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-ink-900" data-id="element-174" data-mp="tz6f3ze8x889">
            <span className="h-1.5 w-1.5 rounded-full bg-sun-400" aria-hidden="true" data-id="element-175" data-mp="k2jv1ctqul7a" />
            Last <span className="text-gold-600" data-id="element-176" data-mp="2vqorh0ulwgki">{remainingSpots} spots</span> on sale
            <span className="h-1.5 w-1.5 rounded-full bg-sun-400" aria-hidden="true" data-id="element-177" data-mp="30p9qiwy4gu7d" />
          </p>

          <div className="mt-3" data-id="element-178" data-mp="35ocusf6a2wea">
            <OfferCountdown data-id="element-179" data-mp="35pe9ctmi610r" />
          </div>

          <div className="mt-6 border-t border-cream-300 pt-2" data-id="element-180" data-mp="35mibf5annccb">
            <button type="button" onClick={() => setIsGuaranteeOpen(open => !open)} aria-expanded={isGuaranteeOpen} className="flex w-full items-center gap-3 py-4 text-left" data-id="element-181" data-mp="39on52413s8yy">
              <span className="flex-1 text-[16px] font-medium text-ink-900" data-id="element-182" data-mp="2m0dl77ng9vag">
                Hands-On Guarantee, 7 days
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-ink-500 transition-transform ${isGuaranteeOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-183" data-mp="2qs3zfopid375" />
            </button>
            {isGuaranteeOpen ? <p className="pb-4 pr-2 text-[15.5px] leading-[1.65] text-ink-500" data-id="element-184" data-mp="31kx6rmy5iaes">
                Try the projects with your child for 7 days. If it is not what your family needed,
                write to us and we refund every cent — you keep the bonuses.
              </p> : null}
          </div>
        </div>
      </div>

      <CheckoutCover isVisible={isCovered} fallbackUrl={checkoutUrl} data-id="element-185" data-mp="36d6ztbukbqla" />
    </section>;
}